/**
 * Alshallal Pastry Company HR System - OCR Tab Component
 * Phase 15: OCR-Assisted Document Data Extraction
 * Phase 15.1: LLM-Assisted Extraction Enhancement
 * 
 * Displays OCR suggestions with human-in-the-loop confirmation workflow.
 * Shows extracted fields with confidence levels, source indicators, and allows accept/edit/reject.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  Scan,
  CheckCircle,
  XCircle,
  Edit3,
  AlertTriangle,
  Shield,
  Clock,
  RefreshCw,
  Loader2,
  FileText,
  Eye,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Cpu,
  Zap,
  Bot
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status styles
const OCR_STATUS_STYLES = {
  queued: { bg: 'bg-slate-100 text-slate-700', icon: Clock, label: { en: 'Queued', ar: 'في الانتظار' } },
  processing: { bg: 'bg-blue-100 text-blue-700', icon: RefreshCw, label: { en: 'Processing', ar: 'قيد المعالجة' } },
  completed: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: { en: 'Completed', ar: 'مكتمل' } },
  failed: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: { en: 'Failed', ar: 'فشل' } },
};

const SUGGESTION_STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-800', icon: Clock },
  accepted: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
  edited: { bg: 'bg-blue-100 text-blue-800', icon: Edit3 },
  rejected: { bg: 'bg-red-100 text-red-800', icon: XCircle },
};

// Source styles (Phase 15.1)
const SOURCE_STYLES = {
  ocr: { bg: 'bg-slate-100 text-slate-700', icon: Scan, label: { en: 'OCR', ar: 'OCR' } },
  llm: { bg: 'bg-purple-100 text-purple-700', icon: Bot, label: { en: 'LLM', ar: 'LLM' } },
  combined: { bg: 'bg-indigo-100 text-indigo-700', icon: Zap, label: { en: 'Enhanced', ar: 'محسّن' } },
  rules: { bg: 'bg-cyan-100 text-cyan-700', icon: Cpu, label: { en: 'Rules', ar: 'قواعد' } },
};

// Confidence colors
const getConfidenceColor = (confidence) => {
  if (confidence >= 0.85) return 'text-green-600 bg-green-50';
  if (confidence >= 0.60) return 'text-amber-600 bg-amber-50';
  return 'text-red-600 bg-red-50';
};

const getConfidenceLabel = (confidence, language) => {
  if (confidence >= 0.85) return language === 'ar' ? 'ثقة عالية' : 'High Confidence';
  if (confidence >= 0.60) return language === 'ar' ? 'ثقة متوسطة' : 'Medium Confidence';
  return language === 'ar' ? 'ثقة منخفضة' : 'Low Confidence';
};

const DocumentOCRTab = ({ documentId, documentName, onRefresh }) => {
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();

  // State
  const [ocrJob, setOcrJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Action dialog
  const [actionDialog, setActionDialog] = useState({ 
    open: false, 
    suggestion: null, 
    action: null 
  });
  const [actionReason, setActionReason] = useState('');
  const [editValue, setEditValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Raw text visibility
  const [showRawText, setShowRawText] = useState(false);
  const [rawText, setRawText] = useState(null);
  const [fetchingRawText, setFetchingRawText] = useState(false);

  // RBAC
  const canUseOCR = hasRole('hr_manager', 'hr_admin', 'super_admin');

  // Fetch OCR status
  const fetchOCRStatus = useCallback(async () => {
    if (!documentId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_URL}/documents/${documentId}/ocr`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.has_ocr) {
        setOcrJob(response.data.job);
      } else {
        setOcrJob(null);
      }
    } catch (err) {
      console.error('Failed to fetch OCR status:', err);
      if (err.response?.status === 403) {
        setError(isRTL ? 'لا تملك صلاحية الوصول لميزات OCR' : 'You do not have access to OCR features');
      } else if (err.response?.status !== 404) {
        setError(err.response?.data?.detail?.error || 'Failed to load OCR data');
      }
    } finally {
      setLoading(false);
    }
  }, [documentId, token, isRTL]);

  useEffect(() => {
    if (canUseOCR) {
      fetchOCRStatus();
    } else {
      setLoading(false);
    }
  }, [fetchOCRStatus, canUseOCR]);

  // Trigger OCR
  const handleTriggerOCR = async () => {
    try {
      setTriggering(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/documents/${documentId}/ocr/trigger`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.job) {
        setOcrJob(response.data.job);
      }
      
      if (!response.data.success) {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Failed to trigger OCR:', err);
      setError(err.response?.data?.detail?.error || 'Failed to trigger OCR');
    } finally {
      setTriggering(false);
    }
  };

  // Retry OCR
  const handleRetryOCR = async () => {
    try {
      setRetrying(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/documents/${documentId}/ocr/retry`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.job) {
        setOcrJob(response.data.job);
      }
      
      if (!response.data.success) {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Failed to retry OCR:', err);
      setError(err.response?.data?.detail?.error || 'Failed to retry OCR');
    } finally {
      setRetrying(false);
    }
  };

  // Fetch raw text
  const handleFetchRawText = async () => {
    try {
      setFetchingRawText(true);
      
      const response = await axios.get(
        `${API_URL}/documents/${documentId}/ocr?include_raw_text=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRawText(response.data.raw_text || null);
      setShowRawText(true);
    } catch (err) {
      console.error('Failed to fetch raw text:', err);
      setError('Failed to fetch raw text');
    } finally {
      setFetchingRawText(false);
    }
  };

  // Handle suggestion action
  const handleSuggestionAction = async () => {
    if (!actionDialog.suggestion || actionReason.length < 10) return;

    const { suggestion, action } = actionDialog;

    try {
      setSubmitting(true);
      setError(null);

      let endpoint = '';
      let payload = {};

      switch (action) {
        case 'accept':
          endpoint = `${API_URL}/documents/${documentId}/ocr/suggestions/${suggestion.field_name}/accept`;
          payload = { reason: actionReason };
          break;
        case 'edit':
          endpoint = `${API_URL}/documents/${documentId}/ocr/suggestions/${suggestion.field_name}/edit`;
          payload = { new_value: editValue, reason: actionReason };
          break;
        case 'reject':
          endpoint = `${API_URL}/documents/${documentId}/ocr/suggestions/${suggestion.field_name}/reject`;
          payload = { reason: actionReason };
          break;
        default:
          return;
      }

      await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Close dialog and refresh
      setActionDialog({ open: false, suggestion: null, action: null });
      setActionReason('');
      setEditValue('');
      fetchOCRStatus();
      
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Action failed:', err);
      setError(err.response?.data?.detail?.error || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Open action dialog
  const openActionDialog = (suggestion, action) => {
    setActionDialog({ open: true, suggestion, action });
    setActionReason('');
    setEditValue(action === 'edit' ? suggestion.suggested_value : '');
  };

  // Render no access state
  if (!canUseOCR) {
    return (
      <Card className="p-8 text-center">
        <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          {isRTL ? 'صلاحية محدودة' : 'Access Restricted'}
        </h3>
        <p className="text-slate-600">
          {isRTL 
            ? 'ميزات OCR متاحة فقط لمدير الموارد البشرية والمسؤول الأعلى'
            : 'OCR features are only available to HR Admin and Super Admin roles'}
        </p>
      </Card>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Render no OCR state
  if (!ocrJob) {
    return (
      <Card className="p-8 text-center" data-testid="ocr-empty-state">
        <Scan className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          {isRTL ? 'لم يتم تشغيل OCR' : 'OCR Not Run'}
        </h3>
        <p className="text-slate-600 mb-6">
          {isRTL 
            ? 'قم بتشغيل OCR لاستخراج البيانات من هذا المستند تلقائياً'
            : 'Run OCR to automatically extract data from this document'}
        </p>
        <Button 
          onClick={handleTriggerOCR} 
          disabled={triggering}
          className="gap-2"
        >
          {triggering ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> {isRTL ? 'جاري المعالجة...' : 'Processing...'}</>
          ) : (
            <><Sparkles className="h-4 w-4" /> {isRTL ? 'تشغيل OCR' : 'Run OCR'}</>
          )}
        </Button>
        
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}
      </Card>
    );
  }

  // Get status info
  const statusInfo = OCR_STATUS_STYLES[ocrJob.status] || OCR_STATUS_STYLES.queued;
  const StatusIcon = statusInfo.icon;
  const statusLabel = statusInfo.label[language] || statusInfo.label.en;

  return (
    <div className="space-y-4" data-testid="document-ocr-tab">
      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* OCR Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="flex items-center gap-2">
                <Scan className="h-5 w-5" />
                {isRTL ? 'نتائج OCR' : 'OCR Results'}
              </CardTitle>
              <Badge className={statusInfo.bg}>
                <StatusIcon className={cn("h-3 w-3 me-1", ocrJob.status === 'processing' && "animate-spin")} />
                {statusLabel}
              </Badge>
              {/* LLM Enhanced Badge (Phase 15.1) */}
              {ocrJob.llm_enabled && ocrJob.llm_provider && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge className="bg-purple-100 text-purple-700">
                        <Bot className="h-3 w-3 me-1" />
                        {isRTL ? 'محسّن بالذكاء' : 'LLM Enhanced'}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{ocrJob.llm_provider} / {ocrJob.llm_model}</p>
                      <p className="text-xs text-slate-400">v{ocrJob.llm_prompt_version}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchOCRStatus} disabled={loading}>
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
              {ocrJob.status === 'failed' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetryOCR}
                  disabled={retrying}
                >
                  {retrying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>{isRTL ? 'إعادة المحاولة' : 'Retry'}</>
                  )}
                </Button>
              )}
            </div>
          </div>
          <CardDescription>
            {isRTL 
              ? `تم الاستخراج في ${format(new Date(ocrJob.created_at), 'PPp')}`
              : `Extracted on ${format(new Date(ocrJob.created_at), 'PPp')}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold text-slate-900">{ocrJob.total_suggestions || 0}</div>
              <div className="text-xs text-slate-600">{isRTL ? 'إجمالي' : 'Total'}</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-600">{ocrJob.pending_count || 0}</div>
              <div className="text-xs text-slate-600">{isRTL ? 'قيد المراجعة' : 'Pending'}</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{(ocrJob.accepted_count || 0) + (ocrJob.edited_count || 0)}</div>
              <div className="text-xs text-slate-600">{isRTL ? 'مقبول' : 'Accepted'}</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{ocrJob.rejected_count || 0}</div>
              <div className="text-xs text-slate-600">{isRTL ? 'مرفوض' : 'Rejected'}</div>
            </div>
          </div>

          {/* Failed state */}
          {ocrJob.status === 'failed' && ocrJob.error_message && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                <XCircle className="h-4 w-4" />
                {isRTL ? 'فشل OCR' : 'OCR Failed'}
              </div>
              <p className="text-red-600 text-sm">{ocrJob.error_message}</p>
            </div>
          )}

          {/* Suggestions List */}
          {ocrJob.suggestions && ocrJob.suggestions.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900 mb-3">
                {isRTL ? 'الحقول المستخرجة' : 'Extracted Fields'}
              </h4>
              
              {ocrJob.suggestions.map((suggestion, index) => (
                <SuggestionCard
                  key={suggestion.field_name}
                  suggestion={suggestion}
                  language={language}
                  isRTL={isRTL}
                  onAccept={() => openActionDialog(suggestion, 'accept')}
                  onEdit={() => openActionDialog(suggestion, 'edit')}
                  onReject={() => openActionDialog(suggestion, 'reject')}
                />
              ))}
            </div>
          )}

          {/* Raw Text Collapsible */}
          <Collapsible open={showRawText} onOpenChange={setShowRawText} className="mt-6">
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full justify-between" onClick={!rawText ? handleFetchRawText : undefined}>
                <span className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  {isRTL ? 'النص الخام المستخرج' : 'Raw Extracted Text'}
                </span>
                {fetchingRawText ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : showRawText ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-3">
              {rawText ? (
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <pre className="text-sm text-slate-700 whitespace-pre-wrap font-mono">{rawText}</pre>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-lg border text-center text-slate-500">
                  {isRTL ? 'لا يوجد نص خام متاح' : 'No raw text available'}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onOpenChange={(open) => !open && setActionDialog({ open: false, suggestion: null, action: null })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={cn(
              actionDialog.action === 'accept' && 'text-green-600',
              actionDialog.action === 'edit' && 'text-blue-600',
              actionDialog.action === 'reject' && 'text-red-600'
            )}>
              {actionDialog.action === 'accept' && (isRTL ? 'قبول الاقتراح' : 'Accept Suggestion')}
              {actionDialog.action === 'edit' && (isRTL ? 'تعديل الاقتراح' : 'Edit Suggestion')}
              {actionDialog.action === 'reject' && (isRTL ? 'رفض الاقتراح' : 'Reject Suggestion')}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.suggestion && (
                <span className="flex items-center gap-2">
                  <strong>{language === 'ar' && actionDialog.suggestion.display_name_ar 
                    ? actionDialog.suggestion.display_name_ar 
                    : actionDialog.suggestion.display_name}</strong>
                  {actionDialog.suggestion.is_high_risk && (
                    <Badge variant="destructive" className="text-xs">
                      <Shield className="h-3 w-3 me-1" />
                      {isRTL ? 'حقل حساس' : 'High Risk'}
                    </Badge>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Show current value */}
            {actionDialog.suggestion && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-xs text-slate-500">
                  {isRTL ? 'القيمة المقترحة' : 'Suggested Value'}
                </Label>
                <p className="font-mono text-sm mt-1">{actionDialog.suggestion.suggested_value}</p>
              </div>
            )}

            {/* Edit value input */}
            {actionDialog.action === 'edit' && (
              <div className="space-y-2">
                <Label>{isRTL ? 'القيمة الجديدة' : 'New Value'}</Label>
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={isRTL ? 'أدخل القيمة الصحيحة...' : 'Enter correct value...'}
                />
              </div>
            )}

            {/* Reason input */}
            <div className="space-y-2">
              <Label>
                {isRTL ? 'السبب (مطلوب - 10 أحرف على الأقل)' : 'Reason (required - min 10 characters)'}
              </Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={
                  actionDialog.action === 'accept' 
                    ? (isRTL ? 'لماذا تقبل هذه القيمة؟' : 'Why are you accepting this value?')
                    : actionDialog.action === 'edit'
                    ? (isRTL ? 'لماذا تعدل هذه القيمة؟' : 'Why are you editing this value?')
                    : (isRTL ? 'لماذا ترفض هذه القيمة؟' : 'Why are you rejecting this value?')
                }
                rows={3}
              />
              {actionReason.length > 0 && actionReason.length < 10 && (
                <p className="text-sm text-amber-600">
                  {isRTL ? `${10 - actionReason.length} أحرف متبقية` : `${10 - actionReason.length} more characters needed`}
                </p>
              )}
            </div>

            {/* High-risk warning */}
            {actionDialog.suggestion?.is_high_risk && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  {isRTL 
                    ? 'هذا حقل عالي الخطورة. تحقق بعناية قبل التأكيد.'
                    : 'This is a high-risk field. Verify carefully before confirming.'}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setActionDialog({ open: false, suggestion: null, action: null })}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSuggestionAction}
              disabled={
                actionReason.length < 10 || 
                submitting ||
                (actionDialog.action === 'edit' && !editValue.trim())
              }
              className={cn(
                actionDialog.action === 'accept' && 'bg-green-600 hover:bg-green-700',
                actionDialog.action === 'edit' && 'bg-blue-600 hover:bg-blue-700',
                actionDialog.action === 'reject' && 'bg-red-600 hover:bg-red-700'
              )}
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('loading')}</>
              ) : (
                <>
                  {actionDialog.action === 'accept' && (isRTL ? 'قبول' : 'Accept')}
                  {actionDialog.action === 'edit' && (isRTL ? 'حفظ التعديل' : 'Save Edit')}
                  {actionDialog.action === 'reject' && (isRTL ? 'رفض' : 'Reject')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Suggestion Card Component
const SuggestionCard = ({ suggestion, language, isRTL, onAccept, onEdit, onReject }) => {
  const statusStyle = SUGGESTION_STATUS_STYLES[suggestion.status] || SUGGESTION_STATUS_STYLES.pending;
  const StatusIcon = statusStyle.icon;
  const confidenceColor = getConfidenceColor(suggestion.confidence);
  const confidenceLabel = getConfidenceLabel(suggestion.confidence, language);
  const isPending = suggestion.status === 'pending';
  
  // Source info (Phase 15.1)
  const sourceStyle = SOURCE_STYLES[suggestion.source] || SOURCE_STYLES.ocr;
  const SourceIcon = sourceStyle.icon;
  const sourceLabel = sourceStyle.label[language] || sourceStyle.label.en;

  return (
    <Card className={cn(
      "p-4",
      suggestion.is_high_risk && "border-amber-300 bg-amber-50/50"
    )}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Field name and badges */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="font-medium text-slate-900">
              {language === 'ar' && suggestion.display_name_ar 
                ? suggestion.display_name_ar 
                : suggestion.display_name}
            </span>
            {suggestion.is_high_risk && (
              <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                <Shield className="h-3 w-3 me-1" />
                {isRTL ? 'حساس' : 'Sensitive'}
              </Badge>
            )}
            <Badge className={statusStyle.bg}>
              <StatusIcon className="h-3 w-3 me-1" />
              {suggestion.status}
            </Badge>
            {/* Source Badge (Phase 15.1) */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge className={cn(sourceStyle.bg, "text-xs")}>
                    <SourceIcon className="h-3 w-3 me-1" />
                    {sourceLabel}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isRTL ? 'مصدر الاستخراج' : 'Extraction Source'}: {suggestion.source}</p>
                  {suggestion.llm_rationale && (
                    <p className="text-xs text-slate-400 mt-1">{suggestion.llm_rationale}</p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Value */}
          <div className="font-mono text-sm bg-white p-2 rounded border">
            {suggestion.status === 'edited' && suggestion.accepted_value
              ? suggestion.accepted_value
              : suggestion.suggested_value}
          </div>

          {/* Confidence and source */}
          <div className="flex items-center gap-4 mt-2 text-xs flex-wrap">
            <span className={cn("px-2 py-0.5 rounded", confidenceColor)}>
              {(suggestion.confidence * 100).toFixed(0)}% - {confidenceLabel}
            </span>
            {/* Show individual confidence scores if available (Phase 15.1) */}
            {suggestion.llm_confidence && (
              <span className="text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                LLM: {(suggestion.llm_confidence * 100).toFixed(0)}%
              </span>
            )}
            {suggestion.ocr_confidence && suggestion.source === 'combined' && (
              <span className="text-slate-600 bg-slate-50 px-2 py-0.5 rounded">
                OCR: {(suggestion.ocr_confidence * 100).toFixed(0)}%
              </span>
            )}
            {suggestion.source_reference && (
              <span className="text-slate-500">{suggestion.source_reference}</span>
            )}
          </div>

          {/* Processed info */}
          {suggestion.status !== 'pending' && suggestion.accepted_by_email && (
            <div className="mt-2 text-xs text-slate-500">
              {isRTL ? 'تم بواسطة' : 'Processed by'}: {suggestion.accepted_by_email}
              {suggestion.reason && ` - ${suggestion.reason}`}
            </div>
          )}
        </div>

        {/* Actions */}
        {isPending && (
          <div className="flex flex-col gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8"
              onClick={onAccept}
              title={isRTL ? 'قبول' : 'Accept'}
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8"
              onClick={onEdit}
              title={isRTL ? 'تعديل' : 'Edit'}
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
              onClick={onReject}
              title={isRTL ? 'رفض' : 'Reject'}
            >
              <XCircle className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default DocumentOCRTab;
